import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Box, Stack, Typography, TextField, Button, CircularProgress, Paper, Checkbox, Skeleton } from '@mui/material';
import { neutral } from '_lex/_foundations/phxColors';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import Properties from './Properties';
import ContentReferences from './PolicyReferences';
import { DTBLibraryContext } from '../../context';
import parse from 'html-react-parser';
import { useNavigate, useParams } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import SearchResults from './SearchResults';
import DTBPreface from '../../_components/DTBPreface';
import DTBSections from '../../_components/DTBSections';
import useIssuedManualSearch from 'views/App/Portal/Policy/SearchManuals/_data_source/useIssuedManualSearch';
import { ManualSearchResult } from 'views/App/Portal/Policy/_types/search';
import { getIssuedManual } from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import type { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';
import { AppContext } from 'views/App/context';
import { updateDTB } from '../../_data_sources/manageDTB';
import { enqueueSnackbar } from 'notistack';
import { debounce } from 'lodash';

const ContentReference: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading, isError, editedData, setEditedData } = useContext(DTBLibraryContext);
  const { currentOrganizationId: orgId } = useContext(AppContext);
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(false);

  // Local state for search and selection
  const [searchInputValue, setSearchInputValue] = useState('');
  const [localInputValue, setLocalInputValue] = useState('');
  const [selected, setSelected] = useState<ManualSearchResult[]>([]);
  const [active, setActive] = useState<ManualSearchResult | null>(null);
  const [selectedManual, setSelectedManual] = useState<Manual | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<any | null>(null);
  const [selectedPreface, setSelectedPreface] = useState<any | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [scrollId, setScrollId] = useState<string | null>(null);

  // Use the issued manual search hook
  const searchQuery = useIssuedManualSearch<ManualSearchResult>(
    searchInputValue,
    { manualId: editedData?.manual?.id },
    { enabled: searchInputValue.length > 0 && !!editedData?.manual?.id }
  );
  const searchResults = searchQuery.data?.pages.flatMap((page) => page.content) || [];
  const loading = searchQuery.isLoading;

  // Fetch up to 5 pages of results to ensure we have enough data after filtering
  React.useEffect(() => {
    if (searchQuery.data && searchQuery.hasNextPage && searchQuery.data.pages.length < 5) {
      searchQuery.fetchNextPage();
    }
  }, [searchQuery.data, searchQuery.hasNextPage, searchQuery.fetchNextPage]);

  // Filter search results to only include policies (with heading) - manual ID filtering is now done server-side
  const filteredSearchResults = React.useMemo(() => {
    return searchResults.filter((result) => !!result.heading);
  }, [searchResults]);

  // Debounced search function
  const debouncedSetSearchInput = useCallback(
    debounce((value: string) => {
      setSearchInputValue(value);
    }, 300),
    []
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalInputValue(value);
    if (value.length > 2) {
      // Only debounce if value is at least 3 characters
      debouncedSetSearchInput(value);
    }
  };

  // Helper to normalize contentUri to always include xref:// protocol
  const normalizeContentUri = (uri: string): string => {
    if (uri.startsWith('xref://')) {
      return uri;
    }
    if (uri.startsWith('/manuals/')) {
      return `xref:/${uri}`;
    }
    if (uri.startsWith('manuals/')) {
      return `xref://${uri}`;
    }
    return uri;
  };

  const handleSelect = (item: ManualSearchResult) => {
    const normalizedItem = {
      ...item,
      contentUri: normalizeContentUri(item.contentUri),
    };
    setSelected((prev) =>
      prev.some((p) => p.contentUri === normalizedItem.contentUri)
        ? prev.filter((p) => p.contentUri !== normalizedItem.contentUri)
        : [...prev, normalizedItem]
    );
  };

  // Parse manualId and contentId from contentUri
  const parseContentUri = (contentUri: string) => {
    const match = contentUri.match(/(?:xref:\/\/)?manuals?\/([^/]+)\/content\/([^/]+)/);
    if (match) {
      return { manualId: match[1], contentId: match[2] };
    }
    return null;
  };

  // Find policy or preface in manual
  const findPolicyOrPreface = (manual: Manual, contentId: string) => {
    // Check prefaces
    const preface = manual.prefaces?.find((p) => p.id === contentId);
    if (preface) return { type: 'preface', content: preface };
    // Check policies in chapters
    for (const chapter of manual.chapters || []) {
      for (const policy of chapter.policies || []) {
        if (policy.id === contentId) return { type: 'policy', content: policy };
        // Check sections
        for (const section of policy.sections || []) {
          if (section.id === contentId) return { type: 'policy', content: policy };
          // Check subsections
          for (const sub of section.subSections || []) {
            if (sub.id === contentId) return { type: 'policy', content: policy };
          }
        }
      }
    }
    return null;
  };

  // When a result is clicked, fetch manual and set preview
  const handlePreview = async (item: ManualSearchResult) => {
    setActive(item);
    setIsContentLoading(true);
    setSelectedManual(null);
    setSelectedPolicy(null);
    setSelectedPreface(null);
    setScrollId(null);
    const parsed = parseContentUri(item.contentUri);
    if (!parsed) {
      setIsContentLoading(false);
      return;
    }
    setScrollId(parsed.contentId);
    try {
      const manual = await getIssuedManual(parsed.manualId);
      setSelectedManual(manual);
      const found = findPolicyOrPreface(manual, parsed.contentId);
      if (found) {
        if (found.type === 'policy') {
          setSelectedPolicy(found.content);
          setSelectedPreface(null);
        } else {
          setSelectedPreface(found.content);
          setSelectedPolicy(null);
        }
      } else {
        setSelectedPolicy(null);
        setSelectedPreface(null);
      }
    } catch (e) {
      setSelectedManual(null);
      setSelectedPolicy(null);
      setSelectedPreface(null);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleRemoveSelected = (item: ManualSearchResult) => {
    setSelected((prev) => prev.filter((p) => p.contentUri !== item.contentUri));
  };

  const handleComplete = async () => {
    if (!editedData || !orgId || !id) return;
    setIsSaving(true);
    const contentReferences = selected.map((item) => ({
      contentUri: item.contentUri,
      name: [item.heading, item.title].filter(Boolean).join(': '),
    }));
    try {
      await updateDTB(orgId, id, { ...editedData, contentReferences });
      setEditedData((prev: any) => (prev ? { ...prev, contentReferences } : prev));
      enqueueSnackbar(t('dtbs.library.detail.linkAssets.success'), { variant: 'success' });
      navigate(-1);
    } catch (e: any) {
      enqueueSnackbar(t('dtbs.library.detail.linkAssets.error'), { variant: 'error', autoHideDuration: 20000 });
    } finally {
      setIsSaving(false);
    }
  };

  // Centralized selection helpers
  const isAssetSelected = (id: string) => selected.some((item) => item.contentUri.endsWith(id));

  // Construct proper contentUri from manual ID and content ID
  const constructContentUri = (contentId: string): string => {
    if (!editedData?.manual?.id) return contentId;
    return `xref://manuals/${editedData.manual.id}/content/${contentId}`;
  };

  const toggleAsset = (id: string, label: string) => {
    const contentUri = constructContentUri(id);

    if (isAssetSelected(id)) {
      setSelected((prev) => prev.filter((item) => !item.contentUri.endsWith(id)));
    } else {
      // Try to find the original search result to preserve all metadata
      const found = searchResults.find((item) => item.contentUri.endsWith(id));
      if (found) {
        setSelected((prev) => [...prev, { ...found, contentUri: normalizeContentUri(found.contentUri) }]);
      } else {
        // Construct a new item with proper contentUri
        const colonIndex = label.indexOf(': ');
        const heading = colonIndex !== -1 ? label.slice(0, colonIndex) : null;
        const title = colonIndex !== -1 ? label.slice(colonIndex + 2) : label;
        setSelected((prev) => [
          ...prev,
          {
            contentUri,
            title,
            heading,
            manualName: editedData?.manual?.name || '',
            highlights: undefined,
          } as ManualSearchResult,
        ]);
      }
    }
  };

  // Render preview for selected/active item
  const renderPreview = () => {
    if (isContentLoading) {
      return (
        <Stack spacing={2}>
          <Skeleton
            variant='rectangular'
            height={28}
            width='50%'
          />
          <Skeleton variant='text' />
          <Skeleton variant='text' />
          <Skeleton variant='text' />
        </Stack>
      );
    }
    if (!selectedManual || (!selectedPolicy && !selectedPreface) || !active || !scrollId) {
      return null;
    }
    if (selectedPolicy) {
      // Find the section or subsection matching scrollId
      const allSections = selectedPolicy.sections || [];
      let sectionToShow = allSections.find((section: any) => section.id === scrollId);
      if (!sectionToShow) {
        // Try to find in subSections
        for (const section of allSections) {
          if (section.subSections?.some((sub: any) => sub.id === scrollId)) {
            sectionToShow = section;
            break;
          }
        }
      }
      // If not found, fallback to first section
      if (!sectionToShow && allSections.length > 0) {
        sectionToShow = allSections[0];
      }
      return (
        <DTBSections
          section={undefined}
          allSections={selectedPolicy.sections}
          isAssetSelected={isAssetSelected}
          onToggleAsset={toggleAsset}
          onAssetClick={() => { }}
          activeSectionId={scrollId}
          showAll={true}
        />
      );
    }
    if (selectedPreface) {
      return (
        <DTBPreface
          preface={selectedPreface}
          isAssetSelected={isAssetSelected}
          onToggleAsset={toggleAsset}
          activeSectionId={scrollId}
        />
      );
    }
    return null;
  };

  // Render selected items in right panel (with checkboxes for removal)
  const renderSelectedPanel = () => (
    <Paper sx={{ p: 2, minWidth: 260, background: neutral[50], borderRadius: 2 }}>
      <Typography
        variant='subtitle1'
        sx={{ mb: 2 }}
      >
        {t('dtbs.library.detail.linkAssets.selectedContent') || 'Selected Content'}
      </Typography>
      <Stack spacing={1}>
        {selected.length === 0 && (
          <Typography
            variant='body2'
            color={neutral[400]}
          >
            {t('dtbs.library.detail.linkAssets.noSelectedContent') || 'No content selected.'}
          </Typography>
        )}
        {selected.map((item) => (
          <Box
            key={item.contentUri}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: neutral[100] },
              borderRadius: 1,
              px: 1,
            }}
            onClick={() => handlePreview(item)}
          >
            <Checkbox
              checked={true}
              onChange={(e) => {
                e.stopPropagation();
                handleRemoveSelected(item);
              }}
              sx={{ mr: 1 }}
            />
            <Typography variant='body2'>{[item.heading, item.title].filter(Boolean).join(': ')}</Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );

  // Shrink results box when preview is active
  const resultsBoxHeight = active ? '20vh' : '60vh';

  useEffect(() => {
    if (editedData?.contentReferences) {
      setSelected(
        editedData.contentReferences.map((ref: any) => ({
          contentUri: normalizeContentUri(ref.contentUri),
          title: ref.title ?? ref.name,
          heading: ref.heading ?? null,
          manualName: editedData.manual?.name || '',
          highlights: undefined,
        }))
      );
    }
  }, [editedData?.contentReferences]);

  return (
    <TakeoverLayout
      pageTitle={
        editedData
          ? `${t('dtbs.library.detail.title')} - ${parse(editedData.name || '')}`
          : t('dtbs.library.detail.title')
      }
      backUrl={'/admin/dtbs/library'}
      contentHeaderRight={
        <Button
          variant='contained'
          color='primary'
          disabled={selected.length === 0 || isSaving}
          endIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          onClick={handleComplete}
        >
          {t('dtbs.library.detail.linkAssets.completeLinking')}
        </Button>
      }
      rightDrawer={{
        header: `${editedData?.name ? editedData.name : ''}`,
        content: renderSelectedPanel(),
        noPadding: true,
        wide: true,
      }}
    >
      {isLoading && (
        <Stack
          direction='row'
          justifyContent='center'
          sx={{ mb: 2 }}
        >
          <CircularProgress />
        </Stack>
      )}
      {isError && (
        <Typography
          color='error'
          sx={{ mb: 2 }}
        >
          {t('common.error')}
        </Typography>
      )}
      {!isLoading && !isError && editedData && (
        <Box sx={{ display: 'flex', maxWidth: 1200, mx: 'auto', width: '100%' }}>
          {/* Left: Search and results */}
          <Box sx={{ flex: 2, pr: 2 }}>
            <TextField
              fullWidth
              placeholder='Search by keyword, phrase, or policy/section number'
              value={localInputValue}
              onChange={onChangeSearch}
              InputProps={{ endAdornment: loading ? <CircularProgress size={20} /> : null }}
              sx={{ mb: 3 }}
            />
            <Box
              sx={{
                maxHeight: resultsBoxHeight,
                overflowY: 'auto',
                background: '#fff',
                borderRadius: 2,
                boxShadow: 1,
                p: 2,
                transition: 'max-height 0.3s',
              }}
            >
              {searchResults.length === 0 && !loading ? (
                <Box sx={{ p: 6, textAlign: 'center', color: neutral[400] }}>
                  <Typography variant='body1'>
                    Use the search field above to locate policies or sections to associate. Click on individual results
                    to view content in context.
                  </Typography>
                </Box>
              ) : (
                <SearchResults
                  results={filteredSearchResults}
                  selected={selected}
                  onSelect={handleSelect}
                  onPreview={handlePreview}
                  activeId={active?.contentUri || null}
                />
              )}
            </Box>
            {/* Preview below search results */}
            {active && (
              <Box sx={{ mt: 2, p: 2, background: '#fff', borderRadius: 2, boxShadow: 1 }}>{renderPreview()}</Box>
            )}
          </Box>
        </Box>
      )}
    </TakeoverLayout>
  );
};

export default ContentReference;
